// @ts-check
import store from 'core/store';

/**
 * @typedef {Object} Tokens
 * @property {string} accessToken
 * @property {string} idToken
 * @property {string} bearerToken
 */

/**
 * @typedef {Object} OAuthToken
 * @property {string} secret
 * @property {string} token
 * @property {string} accessToken
 * @property {string} idToken
 * @property {string} bearerToken
 */

const STORAGE_ID = {
  ACCESS_TOKEN: 'sonomaAccessToken',
  ID_TOKEN: 'sonomaIDToken',
  BEARER_TOKEN: 'sonomaBearerToken',
};

/**
 * Saves tokens to local storage
 * @param {Tokens} tokens - The tokens to save
 */
const saveTokensToStorage = (tokens) => {
  try {
    localStorage.setItem(STORAGE_ID.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(STORAGE_ID.ID_TOKEN, tokens.idToken);
    localStorage.setItem(STORAGE_ID.BEARER_TOKEN, tokens.bearerToken);
  } catch (error) {
    console.error('Failed to save tokens to storage:', error);
  }
};

/**
 * Gets tokens from local storage
 * @returns {Promise<Tokens>} The stored tokens
 */
const getTokensFromStorage = async () => {
  const tokens = {
    idToken: localStorage.getItem(STORAGE_ID.ID_TOKEN),
    accessToken: localStorage.getItem(STORAGE_ID.ACCESS_TOKEN),
    bearerToken: localStorage.getItem(STORAGE_ID.BEARER_TOKEN),
  };

  return tokens;
};

/**
 * Fetches OAuth token using token2 endpoint
 * @param {Tokens} tokens - The tokens to use for authentication
 * @returns {Promise<OAuthToken|null>} The OAuth token response or null if failed
 */
const fetchToken2 = async (tokens) => {
  try {
    const response = await fetch('!!_scpUrl_!!token2', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-sm-oauth-token': tokens.bearerToken || '',
      },
      body: JSON.stringify({
        access_token: tokens.accessToken,
        id_token: tokens.idToken,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed request to get oauth token');
    }
    const json = await response.json();
    return {
      ...json,
      accessToken: tokens.accessToken,
    };
  } catch (error) {
    console.error('Failed to get oauth token from token2:', error);
    // Check if it's a network error or timeout
    if (
      error instanceof TypeError ||
      error.message?.includes('Failed to fetch')
    ) {
      error.isNetworkError = true;
    }
    throw error;
  }
};

/**
 * Gets OAuth token using stored tokens
 * @returns {Promise<OAuthToken|null>} The OAuth token or null if failed
 */
const getOauthTokenByStoredTokens = async () => {
  //get tokens from storage
  const tokens = await getTokensFromStorage();

  if (!tokens.idToken || !tokens.accessToken || !tokens.bearerToken) {
    return null;
  }

  try {
    return await fetchToken2(tokens);
  } catch (error) {
    if (error.isNetworkError) {
      throw error;
    }
    return null;
  }
};

/**
 * Gets OAuth token using SID from URL
 * @returns {Promise<OAuthToken|null>} The OAuth token or null if failed
 */
const getOauthTokenBySid = async () => {
  try {
    const params = new URLSearchParams(window.location.search || '');
    const sid = params.get('sid');

    if (!sid) return null;

    // SIDE EFFECT. If there was a sid then clear any dashboards previously
    // held in localStorage. If we clear the dashboards before
    // `saveTokensToStorage` below, then when the navigator loads it should
    // retrieve fresh dashboards.
    window.sessionStorage.removeItem('xm-navigator:nav-elements');

    // Clear sid from URL after getting it
    window.history.replaceState({}, document.title, window.location.pathname);

    // start request to get oauth token
    const response = await fetch(`!!_scpUrl_!!token2session/${sid}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Failed to get session details');
    }

    const jsonResponse = await response.json();
    const tokens = {
      idToken: jsonResponse.sonomaIDToken,
      bearerToken: jsonResponse.sonomaBearerToken,
      accessToken: jsonResponse.sonomaAccessToken,
    };

    // Save the new tokens
    saveTokensToStorage(tokens);

    return {
      ...jsonResponse,
      ...tokens,
    };
  } catch (error) {
    console.error('Error fetching token:', error);
    // Check if it's a network error or timeout
    if (
      error instanceof TypeError ||
      error.message?.includes('Failed to fetch')
    ) {
      error.isNetworkError = true;
      throw error;
    }
    return null;
  }
};

/**
 * Gets token from URL parameters (legacy method)
 * @returns {Promise<OAuthToken|null>} The OAuth token or null if failed
 */
const getTokenFromURL = async () => {
  if (window.location.search.length === 0) return null;

  const params = new URLSearchParams(window.location.search);

  if (
    !params.has('idToken') ||
    !params.has('sonomaToken') ||
    !params.has('bearerToken')
  ) {
    return null;
  }

  // SIDE EFFECT. If there was a sid then clear any dashboards previously
  // held in localStorage. If we clear the dashboards before
  // `saveTokensToStorage` below, then when the navigator loads it should
  // retrieve fresh dashboards.
  window.sessionStorage.removeItem('xm-navigator:nav-elements');

  const idToken = params.get('idToken');
  const accessToken = params.get('sonomaToken');
  const bearerToken = params.get('bearerToken');

  // remove the sonoma token from the url
  window.history.replaceState({}, document.title, window.location.pathname);

  const tokens = {
    idToken,
    accessToken,
    bearerToken,
  };
  // Save the new tokens
  saveTokensToStorage(tokens);

  try {
    return await fetchToken2(tokens);
  } catch (error) {
    if (error.isNetworkError) {
      throw error;
    }
    return null;
  }
};

/**
 * Main Sonoma Auth Check
 * @param {Object} oauth - The OAuth instance
 * @param {Function} oauth.setTokens - Function to set OAuth tokens
 * @returns {Promise<void>}
 */
export const sonomaAuthCheck = async (oauth) => {
  try {
    // old ways
    let oauthToken = await getTokenFromURL();

    if (!oauthToken) {
      // If getting tokens from url fails, try get sid from URL
      oauthToken = await getOauthTokenBySid();

      // If getting token from SID fails, get the token from storage
      if (!oauthToken) {
        oauthToken = await getOauthTokenByStoredTokens();
      }

      // If none works, throw an error
      if (!oauthToken) {
        throw new Error('Failed to get token from sid and url');
      }
    }

    const { secret, token, accessToken } = oauthToken;

    // set the lux flag
    const access = accessToken?.split('.')[1];
    const decoded = JSON.parse(atob(access || ''));
    store.set('lux', decoded?.migrated === 'true' || false);

    oauth.setTokens(token, secret);
  } catch (error) {
    if (error.isNetworkError) {
      // @ts-ignore
      window.hasNetworkError = true; // needed when app is not yet mounted
      window.dispatchEvent(
        new CustomEvent('network-error', {
          detail: {
            error: error.message || 'Network connection failed',
            url: 'authentication',
          },
        })
      );
    }
  }
};
